// ─── Scheda del socio → 🤖 Bot dei soci ──────────────────────────────────────
//
// 🗣️ 24/09/2026, dal mockup approvato (`mockup/invito-bot-scheda-socio-mockup.html`):
// la segreteria manda al socio il link del bot su WhatsApp.
//
// ⭐ **PERCHÉ ESISTE.** Il bot dei soci si apriva solo dal link di un socio già
// dentro, e il primo non aveva da chi riceverlo. Il bot, a chi non conosce,
// dice «chiama la segreteria»: questo è quello che la segreteria fa.
//
// 🔄 **Dal 25/09/2026 (voce 28, mockup `invito-bot-link-unico-mockup.html`) il
// link è UNO SOLO, uguale per tutti**: non si crea, non scade e non si ritira.
// 🔒 Il bot riconosce il socio dal numero condiviso col pulsante «✅ Entra», che è quello
// dell'account Telegram: deve essere il telefono di questa scheda.

import { useCallback, useEffect, useState } from 'react';
import type { InvitoAlBot } from '@pmo/shared';
import { usaInvitoBot } from '../../features/soci/dati.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { AccountTelegram } from './AccountTelegram.js';

const FUSO = 'Europe/Rome';

const quando = (iso: string) => {
  const d = new Date(iso);
  const giorno = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', timeZone: FUSO });
  const ora = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: FUSO });
  return `il ${giorno} alle ${ora}`;
};
const soloOra = (d: Date) =>
  d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: FUSO });

/**
 * 🗣️ Voce 24 (25/09/2026, dal mockup approvato): mandato il messaggio, il
 * riquadro torna corto da solo e dice cosa si è fatto e a che ora. Resta solo
 * finché la scheda è aperta: il gestionale non lo salva, perché aprire WhatsApp
 * non vuol dire che il socio l'abbia ricevuto.
 */
type Mandato = { come: 'whatsapp' | 'copiato'; alle: Date };

/**
 * Il messaggio di registrazione che la Segreteria manda col link generico.
 *
 * 🔄 12/10/2026 (mockup «Messaggio di registrazione», approvato): 🗣️ *«deve essere un
 * messaggio di registrazione»* · *«leva assistente AI e metti sul nostro profilo
 * Telegram»*. Una frase e il link. «Ciao!» senza nome: l'anagrafica parte vuota, e il
 * messaggio va bene per chiunque. Via i cinque punti (li dice il bot dopo l'ingresso,
 * «Cosa puoi fare con me»), i passi «Avvia» ed «Entra» (il pulsante nel bot è un
 * altro), il numero e «se non hai Telegram». La firma resta.
 */
export function messaggioInvito(link: string): string {
  return (
    `Ciao!\n` +
    `Per giocare al Padel Village ASD registrati sul nostro profilo Telegram.\n\n` +
    `Registrati qui 👉 ${link}\n\n` +
    `La Segreteria di Padel Village ASD`
  );
}

/**
 * Il messaggio come lo mostra WhatsApp: `*così*` è grassetto. Nel riquadro si vede
 * il grassetto, e copiato o mandato parte con gli asterischi, che WhatsApp legge.
 */
function comeSuWhatsApp(testo: string): React.ReactNode[] {
  return testo.split(/\*([^*\n]+)\*/).map((pezzo, i) => (i % 2 ? <strong key={i}>{pezzo}</strong> : pezzo));
}

/** `nome` è il nome di battesimo, `nomeIntero` quello della scheda. */
export function InvitoBot({
  id,
  nome,
  nomeIntero,
}: {
  id: string;
  nome: string;
  nomeIntero: string;
}): React.JSX.Element {
  const invito = usaInvitoBot(id);

  return (
    <section className="riquadro bot-socio">
      <h3>Bot dei soci</h3>
      {invito.isPending ? (
        <Attesa cosa="il bot" />
      ) : invito.error ? (
        <Guasto errore={invito.error} />
      ) : invito.data ? (
        <Contenuto id={id} v={invito.data} nome={nome} nomeIntero={nomeIntero} />
      ) : null}
    </section>
  );
}

function Contenuto({
  id,
  v,
  nome,
  nomeIntero,
}: {
  id: string;
  v: InvitoAlBot;
  nome: string;
  nomeIntero: string;
}): React.JSX.Element {
  /** Il messaggio intero si apre a richiesta: la scheda resta corta. */
  const [mostra, setMostra] = useState(false);
  const [mandato, setMandato] = useState<Mandato | null>(null);
  const [copiato, setCopiato] = useState<'fermo' | 'copiato' | 'nolo'>('fermo');
  // Stabile: il conto alla rovescia dopo «Copiato» non riparte a ogni rilettura.
  const onMandato = useCallback((come: Mandato['come']) => {
    setMandato({ come, alle: new Date() });
    setMostra(false);
    setCopiato('fermo');
  }, []);

  useEffect(() => {
    if (copiato === 'fermo') return;
    // Copiato ⇒ si legge «Copiato» un attimo, poi la riga verde. Non riuscito ⇒
    // il messaggio si apre e resta lì, da selezionare a mano.
    const t =
      copiato === 'copiato'
        ? setTimeout(() => onMandato('copiato'), 1500)
        : setTimeout(() => setCopiato('fermo'), 2500);
    return () => clearTimeout(t);
  }, [copiato, onMandato]);

  if (v.inBot) {
    return (
      <>
        <span className="stato dentro">è nel bot</span>
        <p className="piccolo">Si è registrato {v.joinedAt ? quando(v.joinedAt) : ''}.</p>
        {/* 🔒 A7 (28/09/2026): l'account collegato, e lo «Scollega». */}
        <AccountTelegram id={id} nome={nome} />
      </>
    );
  }

  const perche = !v.phoneE164
    ? 'Serve il telefono nella scheda: il bot lo chiede per riconoscerlo. Aggiungilo con «Modifica l’anagrafica».'
    : !v.link
      ? 'Il bot non si è ancora presentato al gestionale, quindi il link non si può comporre. Riprova quando il bot è acceso.'
      : null;
  const testo = v.link ? messaggioInvito(v.link) : '';

  return (
    <>
      <span className="stato fuori">non è nel bot</span>
      {mandato && (
        <p className="piccolo fatto">
          {mandato.come === 'whatsapp' ? 'Messaggio aperto' : 'Messaggio copiato'} alle{' '}
          {soloOra(mandato.alle)}.
        </p>
      )}
      {perche ? (
        <p className="piccolo avviso">{perche}</p>
      ) : (
        <>
          <p className="piccolo tenue">
            Il link è lo stesso per tutti i soci: il bot riconosce {nomeIntero} dal suo numero, {v.phoneE164}.
          </p>
          <p className="link">
            <code>{v.link}</code>
          </p>
        </>
      )}
      {!perche && mostra && <p className="messaggio">{comeSuWhatsApp(testo)}</p>}
      <div className="gesti">
        {perche !== null || !v.phoneE164 ? (
          <button type="button" disabled>
            📤 Manda il messaggio
          </button>
        ) : (
          <a
            className="bottone-pieno"
            href={`https://wa.me/${v.phoneE164.slice(1)}?text=${encodeURIComponent(testo)}`}
            target="_blank"
            rel="noopener noreferrer"
            // WhatsApp si apre nella scheda nuova; qui resta la riga verde.
            onClick={() => onMandato('whatsapp')}
          >
            📤 Manda il messaggio
          </a>
        )}
        <button
          type="button"
          className="bordato"
          disabled={perche !== null}
          onClick={() => {
            // ⚠️ Se la copia non riesce non si finge: il testo si apre, da selezionare.
            const nolo = () => {
              setCopiato('nolo');
              setMostra(true);
            };
            if (!navigator.clipboard) return nolo();
            void navigator.clipboard
              .writeText(testo)
              .then(() => setCopiato('copiato'))
              .catch(nolo);
          }}
        >
          {copiato === 'copiato' ? 'Copiato' : copiato === 'nolo' ? 'Copialo a mano' : 'Copia il messaggio'}
        </button>
        {!perche && (
          <button type="button" className="bordato" onClick={() => setMostra((m) => !m)}>
            {mostra ? 'Nascondi il messaggio' : 'Mostra il messaggio'}
          </button>
        )}
      </div>
    </>
  );
}
